<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'TransactionNotesEditor',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'input']);

  const props = defineProps({
    value: {
      type: Array,
      required: false,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getItems = (...args) => {
    const value = props.value;
    if (value == null || value.length == 0) {
      return [];
    }
    return value;
  };
  const addItem = (...args) => {
    const newItems = [...items.value, newItem()];
    items.value = newItems;
    emitChange();
  };
  const newItem = (...args) => {
    return {
      message: null,
      valid_from: null,
      valid_to: null,
      counter: null,
      max_displays: null,
    };
  };
  const removeItem = (...args) => {
    const [item] = args;
    const newItems = items.value.filter((p) => p !== item);
    if (newItems.length !== items.value.length) {
      items.value = newItems;
      emitChange();
    }
  };
  const emitChange = (...args) => {
    emit('input', items.value);
  };

  const items = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
  }));

  const $$load_items = async () => {
    return getItems();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'items' }, { name: 'ux' }],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: { items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.value,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('value');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.disabled,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('disabled');
    }
  );

  const act_5534025912102772_script_tfg6e47it_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_5534025912102772_script_tfg6e47it_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5534025912102772_script_tfg6e47it_value_callable($event);
      },
    };
  };
  const act_309515315410323_script_vgv0zdrhx_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_309515315410323_script_vgv0zdrhx_getActionArgs = async ($event) => {
    return {
      value: async () => {
        await act_309515315410323_script_vgv0zdrhx_value_callable($event);
      },
    };
  };
  const act_5188750589197515_script_og2x0mkcm_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_5188750589197515_script_og2x0mkcm_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5188750589197515_script_og2x0mkcm_value_callable($event);
      },
    };
  };
  const act_3475726502813997_script_en8bstqox_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_3475726502813997_script_en8bstqox_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3475726502813997_script_en8bstqox_value_callable($event);
      },
    };
  };
  const act_4449693688767071_script_069dfkn4s_value_callable = async (
    $event
  ) => {
    return emitChange();
  };
  const act_4449693688767071_script_069dfkn4s_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4449693688767071_script_069dfkn4s_value_callable($event);
      },
    };
  };
  const act_1548630417156826_script_7gd0o8pll_value_callable = async (
    $event
  ) => {
    return removeItem($event.scope.item);
  };
  const act_1548630417156826_script_7gd0o8pll_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1548630417156826_script_7gd0o8pll_value_callable($event);
      },
    };
  };
  const act_96700093353179_script_7kd6gcnhs_value_callable = async ($event) => {
    return addItem();
  };
  const act_96700093353179_script_7kd6gcnhs_getActionArgs = async ($event) => {
    return {
      value: async () => {
        await act_96700093353179_script_7kd6gcnhs_value_callable($event);
      },
    };
  };

  const act_5534025912102772_script_tfg6e47it = new actionTypes.ScriptAction({
    actionArgs: act_5534025912102772_script_tfg6e47it_getActionArgs,
  });
  const inst_evh_5534025912102772_input_qkn6n9791 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_script_tfg6e47it,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_5534025912102772_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_input_qkn6n9791],
    event: 'input',
  });
  const act_309515315410323_script_vgv0zdrhx = new actionTypes.ScriptAction({
    actionArgs: act_309515315410323_script_vgv0zdrhx_getActionArgs,
  });
  const inst_evh_309515315410323_input_7lcltz71u =
    new actionTypes.EventHandlerImpl({
      action: act_309515315410323_script_vgv0zdrhx,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_309515315410323_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_309515315410323_input_7lcltz71u],
    event: 'input',
  });
  const act_5188750589197515_script_og2x0mkcm = new actionTypes.ScriptAction({
    actionArgs: act_5188750589197515_script_og2x0mkcm_getActionArgs,
  });
  const inst_evh_5188750589197515_input_kigappgst =
    new actionTypes.EventHandlerImpl({
      action: act_5188750589197515_script_og2x0mkcm,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_5188750589197515_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5188750589197515_input_kigappgst],
    event: 'input',
  });
  const act_3475726502813997_script_en8bstqox = new actionTypes.ScriptAction({
    actionArgs: act_3475726502813997_script_en8bstqox_getActionArgs,
  });
  const inst_evh_3475726502813997_input_fdvbvdfvk =
    new actionTypes.EventHandlerImpl({
      action: act_3475726502813997_script_en8bstqox,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_3475726502813997_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3475726502813997_input_fdvbvdfvk],
    event: 'input',
  });
  const act_4449693688767071_script_069dfkn4s = new actionTypes.ScriptAction({
    actionArgs: act_4449693688767071_script_069dfkn4s_getActionArgs,
  });
  const inst_evh_4449693688767071_input_082msyo4e =
    new actionTypes.EventHandlerImpl({
      action: act_4449693688767071_script_069dfkn4s,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_4449693688767071_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4449693688767071_input_082msyo4e],
    event: 'input',
  });
  const act_1548630417156826_script_7gd0o8pll = new actionTypes.ScriptAction({
    actionArgs: act_1548630417156826_script_7gd0o8pll_getActionArgs,
  });
  const inst_evh_1548630417156826_clickPreventStop_7mlfmjzlo =
    new actionTypes.EventHandlerImpl({
      action: act_1548630417156826_script_7gd0o8pll,
      event: 'click.prevent.stop',
      displayName: 'script',
    });
  const inst_evh_1548630417156826_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1548630417156826_clickPreventStop_7mlfmjzlo],
      event: 'click.prevent.stop',
    });
  const act_96700093353179_script_7kd6gcnhs = new actionTypes.ScriptAction({
    actionArgs: act_96700093353179_script_7kd6gcnhs_getActionArgs,
    displayName: 'addItem',
  });
  const inst_evh_96700093353179_clickPreventStop_nbpe5ger2 =
    new actionTypes.EventHandlerImpl({
      action: act_96700093353179_script_7kd6gcnhs,
      event: 'click.prevent.stop',
      displayName: 'addItem',
    });
  const inst_evh_96700093353179_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_96700093353179_clickPreventStop_nbpe5ger2],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_5534025912102772_input_qkn6n9791,
    inst_evh_5534025912102772_input,
    inst_evh_309515315410323_input_7lcltz71u,
    inst_evh_309515315410323_input,
    inst_evh_5188750589197515_input_kigappgst,
    inst_evh_5188750589197515_input,
    inst_evh_3475726502813997_input_fdvbvdfvk,
    inst_evh_3475726502813997_input,
    inst_evh_4449693688767071_input_082msyo4e,
    inst_evh_4449693688767071_input,
    inst_evh_1548630417156826_clickPreventStop_7mlfmjzlo,
    inst_evh_1548630417156826_clickPreventStop,
    inst_evh_96700093353179_clickPreventStop_nbpe5ger2,
    addItem: inst_evh_96700093353179_clickPreventStop_nbpe5ger2,
    inst_evh_96700093353179_clickPreventStop,
  };

  const evh_5534025912102772_input_qkn6n9791 = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_input_qkn6n9791.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_input = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_309515315410323_input_7lcltz71u = async (event, scope) => {
    return await actions.inst_evh_309515315410323_input_7lcltz71u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_309515315410323_input = async (event, scope) => {
    return await actions.inst_evh_309515315410323_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5188750589197515_input_kigappgst = async (event, scope) => {
    return await actions.inst_evh_5188750589197515_input_kigappgst.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5188750589197515_input = async (event, scope) => {
    return await actions.inst_evh_5188750589197515_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3475726502813997_input_fdvbvdfvk = async (event, scope) => {
    return await actions.inst_evh_3475726502813997_input_fdvbvdfvk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3475726502813997_input = async (event, scope) => {
    return await actions.inst_evh_3475726502813997_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4449693688767071_input_082msyo4e = async (event, scope) => {
    return await actions.inst_evh_4449693688767071_input_082msyo4e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4449693688767071_input = async (event, scope) => {
    return await actions.inst_evh_4449693688767071_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1548630417156826_clickPreventStop_7mlfmjzlo = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1548630417156826_clickPreventStop_7mlfmjzlo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1548630417156826_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1548630417156826_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_clickPreventStop_nbpe5ger2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_96700093353179_clickPreventStop_nbpe5ger2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_96700093353179_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div :key="index" class="d-flex flex-col row" v-for="(item, index) in items">
    <div class="col-auto" v-if="items.length > 1">#{{ index + 1 }}</div>
    <div class="col">
      <div class="mb-2">
        <ItpFormInput
          name="message"
          v-model="item.message"
          :value="item.message"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
              (item.message =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event);
            evh_5534025912102772_input($event, { item, index });
          "
          required
          :readonly="readonly"
          :title="
            pgettext(
              'TransactionNotesEditor',
              'The message to include to the invoice.'
            )
          "
        >
        </ItpFormInput>
      </div>
      <div class="row align-items-center">
        <div class="col-3">
          <ItpFormDatetime
            name="valid_from"
            v-model="item.valid_from"
            :readonly="readonly"
            :placeholder="pgettext('TransactionNotesEditor', 'Valid from')"
            :title="pgettext('TransactionNotesEditor', 'Valid from')"
            @input="evh_309515315410323_input($event, { item, index })"
          >
          </ItpFormDatetime>
        </div>
        <div class="col-3">
          <ItpFormDatetime
            name="valid_to"
            v-model="item.valid_to"
            :readonly="readonly"
            :placeholder="pgettext('TransactionNotesEditor', 'Valid to')"
            :title="pgettext('TransactionNotesEditor', 'Valid to')"
            @input="evh_5188750589197515_input($event, { item, index })"
          >
          </ItpFormDatetime>
        </div>
        <div class="col-3">
          <ItpFormInput
            name="counter"
            type="number"
            v-model="item.counter"
            :value="item.counter"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
                (item.counter =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event);
              evh_3475726502813997_input($event, { item, index });
            "
            :readonly="readonly"
            :placeholder="pgettext('TransactionNotesEditor', 'Counter')"
            :title="pgettext('TransactionNotesEditor', 'Counter')"
          >
          </ItpFormInput>
        </div>
        <div class="col-3">
          <ItpFormInput
            name="max_displays"
            type="number"
            v-model="item.max_displays"
            :value="item.max_displays"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
                (item.max_displays =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event);
              evh_4449693688767071_input($event, { item, index });
            "
            :readonly="readonly"
            :placeholder="pgettext('TransactionNotesEditor', 'Max displays')"
            :title="pgettext('TransactionNotesEditor', 'Max displays')"
          >
          </ItpFormInput>
        </div>
      </div>
    </div>
    <div class="col-auto" v-if="!readonly && !disabled">
      <button
        data-bs-dismiss="modal"
        aria-label="Close"
        :title="pgettext('TransactionNotesEditor', 'Remove')"
        class="close"
        @click.prevent.stop="
          evh_1548630417156826_clickPreventStop($event, { item, index })
        "
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  </div>
  <div class="d-flex mt-2 align-items-center" v-if="!readonly && !disabled">
    <div @click.prevent.stop="evh_96700093353179_clickPreventStop($event, {})">
      <ItpLink
        :icon="$config.addIcon"
        :text="pgettext('TransactionNotesEditor', 'Add')"
      >
      </ItpLink>
    </div>
  </div>
</template>
